import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from '../../../../features/auth/authSlice';
import { AuditLogService } from '../services/auditLogService';

/**
 * Custom hook for managing audit logs state and operations
 * Handles data fetching, filtering, pagination, and export functionality
 */
export const useAuditLogs = () => {
  // Core state
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Filter state
  const [filters, setFilters] = useState({
    actionType: '',
    startDate: '',
    endDate: ''
  });

  // Get token from Redux
  const token = useSelector(selectCurrentToken);

  /**
   * Fetch audit logs with current filters and pagination
   */
  const fetchAuditLogs = useCallback(async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      setError(null);

      const options = {
        page,
        limit: pagination.limit,
        ...currentFilters
      };

      console.log('🔍 Fetching audit logs with options:', options);

      const result = await AuditLogService.fetchAuditLogs(options);

      if (result.success) {
        const { auditLogs: logs, pagination: paginationData } = result.data;
        
        console.log('📊 Audit Logs API Response:', result.data);
        console.log('📋 Audit Logs Array:', logs);
        console.log('📄 Pagination:', paginationData);

        setAuditLogs(logs || []);

        // Map backend pagination to frontend format
        if (paginationData) {
          setPagination({
            page: paginationData.currentPage,
            limit: paginationData.limit,
            total: paginationData.totalCount,
            totalPages: paginationData.totalPages
          });
        }
      } else {
        setError(result.error);
        setAuditLogs([]);
      }
    } catch (err) {
      setError('Unexpected error occurred while fetching audit logs');
      setAuditLogs([]);
      console.error('🚨 Unexpected error in fetchAuditLogs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  /**
   * Handle filter changes and auto-apply them
   */
  const handleFilterChange = useCallback((field, value) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    setFilters(newFilters);
    
    // Auto-apply filters when they change (reset to page 1)
    fetchAuditLogs(1, newFilters);
  }, [filters, fetchAuditLogs]);

  /**
   * Apply current filters and reset to first page
   */
  const handleApplyFilters = useCallback(() => {
    fetchAuditLogs(1, filters);
  }, [fetchAuditLogs, filters]);

  /**
   * Clear all filters and fetch first page
   */
  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      actionType: '',
      startDate: '',
      endDate: ''
    };
    setFilters(clearedFilters);
    fetchAuditLogs(1, clearedFilters);
  }, [fetchAuditLogs]);

  /**
   * Handle page changes
   */
  const handlePageChange = useCallback((newPage) => {
    console.log('📄 Page change requested:', newPage);
    fetchAuditLogs(newPage, filters);
  }, [fetchAuditLogs, filters]);

  /**
   * Export audit logs as CSV
   */
  const handleExportCSV = useCallback(async () => {
    try {
      setExportLoading(true);
      setError(null);

      console.log('📤 Exporting audit logs with filters:', filters);

      const result = await AuditLogService.exportAuditLogsCSV(filters);

      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('Unexpected error occurred during export');
      console.error('🚨 Unexpected error in handleExportCSV:', err);
    } finally {
      setExportLoading(false);
    }
  }, [filters]);

  /**
   * Refresh audit logs (refetch with current state)
   */
  const refreshAuditLogs = useCallback(() => {
    fetchAuditLogs(pagination.page, filters);
  }, [fetchAuditLogs, pagination.page, filters]);

  // Initial data fetch when token is available
  useEffect(() => {
    if (token) {
      fetchAuditLogs();
    }
  }, [token, fetchAuditLogs]);

  // Debug logging for audit logs state changes
  useEffect(() => {
    console.log('🔍 Audit Logs State Updated:', auditLogs);
    console.log('📊 Audit Logs Length:', auditLogs.length);
  }, [auditLogs]);

  return {
    // State
    auditLogs,
    loading,
    error,
    exportLoading,
    pagination,
    filters,

    // Actions
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    handlePageChange,
    handleExportCSV,
    refreshAuditLogs,

    // Utilities
    formatDateTime: AuditLogService.formatDateTime,
    getActionBadgeColor: AuditLogService.getActionBadgeColor,
    getActionTypeOptions: AuditLogService.getActionTypeOptions
  };
};

export default useAuditLogs;